import {
  RESOURCE_DOMAIN,
  STUDENT_GROUP_RESOURCE_TYPE,
} from "@modules/editor/service/editor.constant";
import { RESOURCE_DOMAIN_PATH } from "@plugins/common/constant/resource.constant";
import { LOCKABLE_RESOURCE_TYPE } from "@plugins/common/service/lock.constant";
import assignmentApi from "@apis/assignment.api";
import AssignmentEditModal from "@modules/attempt/components/assignment/AssignmentEditModal.vue";
import LockService from "@plugins/common/service/lock.service";
import ErrorMixin from "@plugins/common/mixin/error.mixin";
import SecurityUtils from "@utils/security.utils";
import studentGroupResourceApi from "@apis/student.group.resource.api";
import ResourceEditModal from "@modules/editor/components/lesson/ResourceEditModal.vue";
import lessonEditHistoryApi from "@apis/lesson.edit.open.history.api";
import { ObjectLockModel } from "@plugins/common/types.ts";
import { loadLocaleMessages } from "@plugins/i18n";

export default {
  mixins: [ErrorMixin],
  data() {
    return {
      lockService: null,
      studentGroupResourceType: null,
    };
  },
  methods: {
    resourceEditMixin_isMoeResource(resourceDomain) {
      return resourceDomain === RESOURCE_DOMAIN.MOE.value;
    },
    resourceEditMixin_isCgResource(resourceDomain) {
      return resourceDomain === RESOURCE_DOMAIN.COMMUNITY.value;
    },
    resourceEditMixin_isAssignmentResource(resourceDomain) {
      return resourceDomain === RESOURCE_DOMAIN.ASSIGNMENT.value;
    },
    resourceEditMixin_getLessonUuid(lesson) {
      const resourceDomain = lesson.resourceDomain;
      if (this.resourceEditMixin_isCgResource(resourceDomain)) {
        return lesson?.communityResourceUuid ?? lesson.resourceUuid;
      } else if (this.resourceEditMixin_isMoeResource(resourceDomain)) {
        return lesson?.resourceUuid;
      }
      return lesson.uuid;
    },
    resourceEditMixin_getResourceDomainPath(lesson) {
      const resourceDomain = lesson.resourceDomain;
      if (this.resourceEditMixin_isAssignmentResource(resourceDomain)) {
        return RESOURCE_DOMAIN_PATH[lesson.studentGroupResourceType];
      }
      return RESOURCE_DOMAIN[resourceDomain].path;
    },
    resourceEditMixin_handleEditLesson(resourceType, lesson, responseCallback) {
      this.studentGroupResourceType = lesson.studentGroupResourceType;
      if (resourceType === "ASSIGNMENT_LESSON") {
        switch (lesson.studentGroupResourceType) {
          case "ASSIGNMENT":
            this.resourceEditMixin_handleEditAssignmentResource(lesson, responseCallback);
            break;
          case "CLASS_GROUP_RESOURCE":
            this.resourceEditMixin_handleEditResource(lesson, responseCallback);
            break;
        }
      } else {
        this.resourceEditMixin_handleOpenLesson(lesson, "edit");
        this.resourceEditMixin_handleSaveLessonEditHistory(lesson.uuid, null);
      }
    },
    resourceEditMixin_handleOpenLesson(lesson, mode) {
      const resourceDomain = lesson.resourceDomain;
      // TODO: SLS-11043 (OK), editor, use in detail subpage
      const routeParams = {
        resourceDomain: this.resourceEditMixin_getResourceDomainPath(lesson),
        isAdminPage:
          this.resourceEditMixin_isMoeResource(resourceDomain) ||
          this.resourceEditMixin_isCgResource(resourceDomain)
            ? "admin"
            : null,
        lessonUuid: this.resourceEditMixin_getLessonUuid(lesson),
        mode: mode,
      };

      const route = this.getEditorCoverRoute(routeParams);

      route.query = {
        ...route.query,
        showAuthoringCoPilot: lesson.showAuthoringCoPilot || false,
      };
      if (resourceDomain === "ASSIGNMENT" && lesson.studentGroupResourceType === "ASSIGNMENT") {
        route.query = {
          ...route.query,
          assignmentUuid: lesson?.lessonAssignmentUuid ?? lesson?.resourceUuid,
        };
      }

      if (resourceDomain === RESOURCE_DOMAIN.MOE.value) {
        route.query = {
          ...route.query,
          version: lesson?.uuid ? lesson?.uuid : undefined,
        };
      }
      this.$router.push(route);
    },
    resourceEditMixin_handleEditAssignmentResource(lesson, responseCallback) {
      if (lesson.paused) {
        this.resourceEditMixin_showAssignmentEditModal(lesson);
        responseCallback();
        return;
      }

      const assignmentUuid = lesson?.lessonAssignmentUuid ?? lesson?.resourceUuid;
      this.resourceEditMixin_handlePauseAssignment(assignmentUuid).then((response) => {
        if (response) {
          this.resourceEditMixin_showAssignmentEditModal(lesson);
        }
        responseCallback(response);
      });
    },
    resourceEditMixin_handleEditResource(lesson, responseCallback) {
      if (lesson.paused) {
        this.resourceEditMixin_handleLockServiceAndOpen(lesson);
        responseCallback();
      } else {
        const assignmentUuid = lesson?.lessonAssignmentUuid ?? lesson?.resourceUuid;
        this.resourceEditMixin_handleRemoveAccess(assignmentUuid).then((response) => {
          if (response) {
            this.resourceEditMixin_showResourceEditModal(lesson);
          }
          responseCallback(response);
        });
      }
    },
    resourceEditMixin_handlePauseAssignment(assignmentUuid) {
      return new Promise((resolve) => {
        assignmentApi.pauseLinkedAssignments(assignmentUuid).then(
          (response) => {
            if (response.code === "ok") {
              return resolve(true);
            } else {
              this.passiveApiFailHandler();
              return resolve(false);
            }
          },
          () => {
            this.passiveApiFailHandler();
            return resolve(false);
          },
        );
      });
    },
    resourceEditMixin_handleRemoveAccess(assignmentUuid) {
      return new Promise((resolve) => {
        studentGroupResourceApi.removeStudentAccessResource(assignmentUuid).then(
          (response) => {
            if (response.code === "ok") {
              return resolve(true);
            } else {
              this.passiveApiFailHandler();
              return resolve(false);
            }
          },
          () => {
            this.passiveApiFailHandler();
            return resolve(false);
          },
        );
      });
    },
    resourceEditMixin_handleSaveLessonEditHistory(lessonUuid, assignmentUuid = null) {
      lessonEditHistoryApi.saveLessonEditHistory(lessonUuid, assignmentUuid).then((response) => {
        if (response.code !== "ok") {
          this.passiveApiFailHandler();
        }
      }, this.passiveApiFailHandler);
    },
    resourceEditMixin_showAssignmentEditModal(lesson) {
      this.$modal.show(AssignmentEditModal, {
        hasGamification: lesson.gameEnabled,
        actionPrimary: () => {
          this.resourceEditMixin_handleLockServiceAndOpen(lesson);
        },
      });
    },
    resourceEditMixin_showResourceEditModal(lesson) {
      this.$modal.show(ResourceEditModal, {
        actionPrimary: () => {
          this.resourceEditMixin_handleLockServiceAndOpen(lesson);
        },
      });
    },
    resourceEditMixin_initLockService(lesson) {
      if (this.lockService !== null) return;

      const isClassGroupResource =
        lesson.resourceDomain === RESOURCE_DOMAIN.ASSIGNMENT.value &&
        lesson.studentGroupResourceType === STUDENT_GROUP_RESOURCE_TYPE.CLASS_GROUP_RESOURCE;

      const isMoeResource = this.resourceEditMixin_isMoeResource(lesson.resourceDomain);

      this.lockService = new LockService(
        LOCKABLE_RESOURCE_TYPE.LESSON,
        lesson.uuid,
        isClassGroupResource || isMoeResource
          ? (response) =>
              this.resourceEditMixin_handleLockRetrievalFailWithNewModal(response, lesson)
          : (response) => this.resourceEditMixin_handleLockRetrievalFail(response, lesson),
      );
    },
    resourceEditMixin_handleLockServiceAndOpen(lesson, overrideExistingLock = false) {
      const lessonUuid = lesson.uuid;
      let assignmentUuid = null;

      if (lesson.resourceDomain === "ASSIGNMENT") {
        assignmentUuid = lesson?.lessonAssignmentUuid ?? lesson?.resourceUuid;
      }

      this.resourceEditMixin_initLockService(lesson);
      this.lockService.lockOnce(overrideExistingLock).then(() => {
        this.resourceEditMixin_handleOpenLesson(lesson, "edit");
        this.resourceEditMixin_handleSaveLessonEditHistory(lessonUuid, assignmentUuid);
        return;
      });
    },
    resourceEditMixin_handleLockServiceAndOpenWithUrl(lesson, actionUrl) {
      const lessonUuid = lesson.uuid;
      let assignmentUuid = null;

      if (lesson.resourceDomain === "ASSIGNMENT") {
        assignmentUuid = lesson?.lessonAssignmentUuid ?? lesson?.resourceUuid;
      }

      this.resourceEditMixin_initLockService(lesson);
      this.lockService.lockOnce().then(() => {
        this.resourceEditMixin_handleSaveLessonEditHistory(lessonUuid, assignmentUuid);
        window.location = actionUrl;
        return;
      });
    },
    resourceEditMixin_handleLockRetrievalFail(response, lesson) {
      this.resourceEditMixin_showLessonLockedModal(
        response,
        LOCKABLE_RESOURCE_TYPE.LESSON,
        () => {
          this.resourceEditMixin_handleOpenLesson(lesson, "view");
        },
        () => this.resourceEditMixin_handleOpenLesson(lesson, "view"),
        false,
        () => this.resourceEditMixin_handleLockServiceAndOpen(lesson, true),
      );
    },
    resourceEditMixin_handleLockRetrievalFailWithNewModal(response, lesson) {
      this.resourceEditMixin_showLessonLockedModal(
        response,
        LOCKABLE_RESOURCE_TYPE.LESSON,
        () => {
          this.resourceEditMixin_handleOpenLesson(lesson, "view");
        },
        () => this.resourceEditMixin_handleOpenLesson(lesson, "view"),
        true,
        () => this.resourceEditMixin_handleLockServiceAndOpen(lesson, true),
      );
    },
    // to replace this method with lesson.lock.error.ts - showLessonLockedModal when migrate
    async resourceEditMixin_showLessonLockedModal(
      response: ObjectLockModel,
      currentLockResourceType: string,
      actionPrimaryFn: () => void,
      actionHideRequestFn: () => void,
      showErrorModalByLockableResourceType: boolean,
      overrideExistingLockFn?: () => void,
    ) {
      await loadLocaleMessages("editor.course");

      let title: string;
      let content: string;

      if (response.missingBrowserTabUuid) {
        this.$modal.showMessage("warning", {
          title: this.$i18n.t("api_action_generic_error_title"),
          content: this.$i18n.t("api_action_generic_error_message"),
          primaryBtnLabel: this.$i18n.t("button_ok"),
          use_secondaryButton: false,
        });

        return;
      }

      if (response.lockedBySameUserDifferentTab === true) {
        title = this.$i18n.t("module_locked_in_another_tab_continue_edit_modal_title");
        content = this.$i18n.t("module_locked_in_another_tab_continue_edit_modal_content");

        this.$modal.showMessage("warning", {
          title,
          content,
          primaryBtnLabel: this.$i18n.t("module_locked_in_another_tab_modal_edit_here_button"),
          secondaryBtnLabel: this.$i18n.t("module_locked_in_another_tab_modal_view_only_button"),
          actionPrimary: () => {
            overrideExistingLockFn?.();
          },
          actionSecondary: () => {
            actionPrimaryFn();
          },
          actionHidden: () => {
            // empty function
          },
          actionHideRequest: () => {
            actionHideRequestFn();
          },
        });

        return;
      }

      if (showErrorModalByLockableResourceType) {
        const attemptedType = currentLockResourceType;
        const blockingType = response.resourceTypeLocked;
        const owners = response.resourceLockOwners ?? [];
        const owner = owners[0];
        const ownerName = SecurityUtils.sanitizeText(
          owner?.displayName ? `${owner.displayName} (${owner.name})` : (owner?.name ?? ""),
        );

        if (blockingType === LOCKABLE_RESOURCE_TYPE.LESSON) {
          title = this.$i18n.t("module_locked_modal_title");
          content = this.$i18n.t("module_locked_modal_content", { name: ownerName });
        } else if (
          attemptedType === LOCKABLE_RESOURCE_TYPE.LESSON &&
          blockingType === LOCKABLE_RESOURCE_TYPE.LESSON_ACTIVITY
        ) {
          title = this.$i18n.t("activity_level_edits_only_modal_title");
          content =
            owners.length > 1
              ? this.$i18n.t("activity_level_edits_only_multiple_modal_content")
              : this.$i18n.t("activity_level_edits_only_single_modal_content", { name: ownerName });
        } else {
          title = this.$i18n.t("activity_locked_modal_title");
          content = this.$i18n.t("activity_locked_modal_content", { name: ownerName });
        }
      } else {
        title = this.$i18n.t("resource_locked_modal_title");
        content = this.$i18n.t("resource_locked_modal_content", {
          userName: SecurityUtils.sanitizeText(response.user ?? ""),
        });
      }

      const closeOnlyOnModuleEditLock =
        showErrorModalByLockableResourceType &&
        currentLockResourceType === LOCKABLE_RESOURCE_TYPE.LESSON &&
        (response.resourceTypeLocked === LOCKABLE_RESOURCE_TYPE.LESSON ||
          response.resourceTypeLocked === LOCKABLE_RESOURCE_TYPE.LESSON_ACTIVITY);

      this.$modal.showMessage("warning", {
        title,
        content,
        primaryBtnLabel: this.$i18n.t("button_ok"),
        use_secondaryButton: false,
        actionPrimary: () => {
          if (!showErrorModalByLockableResourceType) {
            actionPrimaryFn();
          }
        },
        actionHidden: () => {
          // empty function
        },
        actionHideRequest: () => {
          if (!closeOnlyOnModuleEditLock) {
            actionHideRequestFn();
          }
        },
      });
    },
  },
};
